<template>
  <NuxtLink
    :to="toPath"
    :target="linkTarget"
    class="experience-card"
    :class="[{ 'bordered-card': hasBorder }, customClass]"
    :style="{ width: cardWidth }"
  >
    <NuxtImg
      loading="lazy"
      v-no-right-click
      :src="imageSrc"
      @error="onImageError"
      :alt="title"
      height="280"
    />
    <div class="content">
      <div class="card-title">
        <div class="flex flex-wrap">
          <span v-for="item in cities" :key="item.id" class="city">
            {{ item.name }}
          </span>
        </div>
        <h4 :title="title">
          {{ title }}
        </h4>
        <LazyProductCardPayLater
          v-if="isDeferredPaymentEligible(hasPayLater, depositPercent)"
          :price="price"
          :currency="currency"
          :deposit-percent="depositPercent"
        />
      </div>
      <div class="rating-and-price">
        <div v-if="rate" class="flex items-center mb-0.5">
          <LazyBaseRatingStars :rate="rate" class="me-2.5" />
          <span class="rate-text">{{ rate }}/5</span>
        </div>
        <div class="price" :class="{ discount: offPercent > 0 }">
          <span v-if="!isNegotiable" class="title">
            {{ t("from") }}
            <span v-if="offPercent > 0" class="real-price">
              {{ formatPrice(marketPrice) + " " + currency }}
            </span>
          </span>
          <span class="final-price">
            {{
              isNegotiable
                ? t("requestDetails")
                : formatPrice(price) + " " + currency
            }}
            <ProductCardAvailabilityType
              v-if="!isNegotiable && !hideAvailabilityType"
              :pricing-type="pricingType"
              :availability-type="availabilityType"
              :duration="duration"
            />
          </span>
        </div>
      </div>
    </div>
    <div
      v-if="hasDiscount && offPercent > 0"
      class="discount-percent"
      :class="discountColors"
    >
      {{ t("save") }}
      {{ offPercent }}%
    </div>
  </NuxtLink>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, watch } from "vue";
import type { PropType } from "vue";
import type { ICity } from "../../types/general";
import { formatPrice, offPercentCalculator, isDeferredPaymentEligible } from "../../composables/helper";

const { t } = useI18n();
const route = useRoute();
const props = defineProps({
  width: { type: Number, required: false, default: null },
  image: { type: String, required: true },
  cities: { type: Array as PropType<ICity[]>, required: true },
  title: { type: String, required: true },
  rate: { type: Number, default: null },
  marketPrice: { type: Number, required: false, default: null }, //price before off - if product has off
  price: { type: Number, required: true, default: null },
  currency: { type: String, required: true },
  toPath: { type: String, default: "/" },
  slug: { type: String, required: true },
  hasBorder: { type: Boolean, default: false },
  isNegotiable: { type: Boolean, default: false },
  hasPayLater: { type: Boolean, default: false },
  depositPercent: { type: Number, default: 0 },
  discountColors: {
    type: String,
    default: "bg-discount-background text-primary-1",
  },
  pricingType: { type: String, default: null },
  availabilityType: { type: String, default: null },
  customClass: { type: String, default: null },
  duration: { type: Number, default: null },
  hideAvailabilityType: { type: Boolean, default: false },
});

const offPercent = ref(offPercentCalculator(props.marketPrice, props.price));
const isdiscover = ref(false);
const localePath = useLocalePath();
watch(
  () => route.path,
  () => {
    isdiscover.value = route.path == localePath("/things-to-do");
  },
  { immediate: true },
);
const cardWidth = computed(() => {
  return props.width ? props.width + "px" : null;
});

const hasDiscount = computed(() => {
  if (props.marketPrice && props.marketPrice > props.price) return true;
  return false;
});
const isDesktop = ref(false);
const handleResize = () => {
  isDesktop.value = window.innerWidth >= 1024;
};
onMounted(() => {
  handleResize();
});
const linkTarget = computed(() => {
  return isDesktop.value ? "_blank" : "_self";
});

const fallbackImage = "product-default.jpg";
const imageSrc = ref(props.image);

const onImageError = () => {
  imageSrc.value = fallbackImage;
};

watch(
  () => props.image,
  (newSrc) => {
    imageSrc.value = newSrc;
  },
);
</script>

<style lang="scss" scoped>
.experience-card {
  @apply block relative border border-gray-2 rounded-[10px] top-0 hover:md:-top-1 transition-all duration-[250ms];
}
.experience-card.dark-mode {
  .card-title .city,
  .rating-and-price .rate-text,
  .rating-and-price .price .final-price,
  .rating-and-price .price .title {
    @apply text-gray-5;
  }
  .card-title h4 {
    @apply text-white;
  }
}

.experience-card.bordered-card img {
  @apply rounded-t-[10px] rounded-b-none;
}
.experience-card img {
  @apply w-full object-cover rounded-t-[10px] rounded-b-none xs:h-[169px] sm:h-[280px];
}

.experience-card .content {
  @apply flex flex-col justify-between h-[170px] py-3 px-2.5;
}

.experience-card .card-title {
  @apply flex flex-col justify-start mb-0.5;
}

.experience-card .card-title .city {
  @apply xs:text-mobile-heading-6 lg:text-heading-6 text-gray-1 mb-1 mr-1 whitespace-nowrap;
  &::after {
    content: ",";
  }
  &:last-child {
    &::after {
      content: "";
    }
  }
}

.experience-card .card-title h4 {
  @apply text-mobile-heading-4 lg:text-heading-4 text-primary-1 line-clamp-2;
}

.experience-card .rating-and-price {
  @apply flex flex-col justify-start;
}

.experience-card .rating-and-price .rate-text {
  @apply text-gray-1 xs:text-mobile-paragraph-1 lg:text-paragraph-1;
}

.experience-card .rating-and-price .price .title {
  @apply xs:text-mobile-paragraph-1 lg:text-paragraph-1 text-gray-1;
}

.experience-card .rating-and-price .price .final-price {
  @apply xs:text-mobile-heading-4 lg:text-heading-4 text-primary-1 whitespace-nowrap;
}

.experience-card .rating-and-price .price.discount {
  @apply xs:max-sm:flex xs:max-sm:flex-col xs:max-sm:justify-start;
}

.experience-card .rating-and-price .price.discount .title {
  @apply text-paragraph-2 inline-block;
}

.experience-card .rating-and-price .price .real-price {
  @apply ltr:mr-1.5 rtl:ml-1.5 relative;
}

.experience-card .rating-and-price .price .real-price::after {
  content: "";
  @apply absolute w-[102%] h-[1px] left-0 top-2/4 border-t border-gray-1;
}

.experience-card .discount-percent {
  @apply rounded-[20px] absolute xs:top-2.5 lg:top-3 xs:left-2.5 lg:left-3 text-heading-5 xs:px-[10px] lg:px-3 py-1;
}
</style>
